import { useEffect, useRef, useState } from "react";

/**
 * Runs a computation in a Web Worker off the main thread, returning the latest result.
 * `url` must be a stable module-level constant. `input` must be memoized by the caller
 * when it is an object — the hook re-posts to the worker whenever the reference changes.
 */
export function useWorker<TInput, TOutput>(url: URL, input: TInput): TOutput | null {
  const workerRef = useRef<Worker | null>(null);
  const [result, setResult] = useState<TOutput | null>(null);

  useEffect(() => {
    const worker = new Worker(url, { type: "module" });
    workerRef.current = worker;
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [url]);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;
    let stale = false;
    const handler = (e: MessageEvent<TOutput>) => {
      if (!stale) setResult(e.data);
    };
    worker.addEventListener("message", handler);
    worker.postMessage(input);
    return () => {
      stale = true;
      worker.removeEventListener("message", handler);
    };
  }, [url, input]);

  return result;
}
